import React from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import InteractiveTable from 'components/InteractiveTable';

interface CapitalContributionsContentProps {
  selectedCommitments: Array<{
    commitmentId: string;
    investorName: string;
    isCarryPartnerCommitment: boolean;
    totalCommitmentLcy: number;
    loanCommitmentLcy: number;
    capitalContributionChange: number;
    capitalCommitmentLcy: number;
    existingCapitalCommitmentLcy?: number;
    isCapitalContributionRuleOverride?: boolean;
    equalisationDate?: CalendarDay | null;
  }>;
  baseCurrency: CurrencyCode;
}

const CapitalContributionsContent: React.FC<
  CapitalContributionsContentProps
> = ({ selectedCommitments, baseCurrency }) => {
  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const hasIds = Boolean(id && selectedVehicleId);

  const equalisationQueryKey = getQueryKey(api.equalisations.get, {
    equalisationId: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationQueryKey],
    }),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const rows = selectedCommitments.map((commitment) => {
    const originalCapital = commitment.existingCapitalCommitmentLcy ?? 0;
    const newCapital = commitment.capitalCommitmentLcy;

    return {
      id: commitment.commitmentId,
      investorName: commitment.investorName,
      isCarryPartnerCommitment: commitment.isCarryPartnerCommitment,
      originalCapital,
      newCapital,
      baseCurrency,
      change: commitment.capitalContributionChange,
      equalisationDate: commitment.equalisationDate,
    };
  });

  const columns = [
    {
      field: 'investorName',
      headerName: 'Investor name',
      renderCell: ({ row }) => row.investorName,
      flex: 1,
    },
    {
      field: 'originalCapital',
      headerName: 'Original capital contribution',
      renderCell: ({ row }) =>
        row.equalisationDate
          ? numberToCurrencyString(row.originalCapital, {
              fallback: '-',
              currency: row.baseCurrency,
            })
          : '-',
      flex: 1,
    },
    {
      field: 'newCapital',
      headerName: 'New capital contribution',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.newCapital, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'change',
      headerName: 'Change',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.change, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
  ];

  return (
    <>
      <InteractiveTable
        id="capital-contributions-table"
        columns={columns}
        rows={rows}
      />
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={!hasIds}
        loading={updateEqualisation.isPending}
      />
    </>
  );
};

export default CapitalContributionsContent;
